import api from './client';
import { API_ENDPOINTS } from './endpoints';

export type TaskType = 'task' | 'bug' | 'feature' | 'improvement' | 'review';
export type TaskStatus = 'open' | 'assigned' | 'in_progress' | 'on_hold' | 'in_review' | 'completed' | 'closed' | 'cancelled';
export type TaskPriority = 'low' | 'medium' | 'high' | 'critical';

export interface TaskComment {
  _id?: string;
  comment: string;
  commented_by_id?: string;
  commented_by_name?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface TaskActivity {
  _id?: string;
  action: string;
  actor_user_id?: string;
  actor_user_name?: string;
  remarks?: string;
  old_values?: unknown;
  new_values?: unknown;
  createdAt?: string;
}

export interface TaskApiItem {
  _id: string;
  custom_task_id?: string;
  title?: string;
  description?: string;
  task_type?: TaskType | string;
  task_status?: TaskStatus | string;
  priority?: TaskPriority | string;
  department_name?: string;
  // Fixed at creation — the department this task originally came from, even
  // after it's been routed to another department's queue (see routeTaskToDepartment).
  origin_department_name?: string;
  created_by_id?: string;
  created_by_name?: string;
  assigned_to_id?: string;
  assigned_to_name?: string;
  assigned_to_email?: string;
  assigned_by_id?: string;
  assigned_by_name?: string;
  assigned_at?: string;
  due_date?: string;
  start_date?: string;
  completed_at?: string;
  closed_at?: string;
  tags?: string[];
  comments?: TaskComment[];
  activity?: TaskActivity[];
  last_action_at?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface TaskListResponse {
  success: boolean;
  statusCode: number;
  message: string;
  data?: {
    tasks?: TaskApiItem[];
    items?: TaskApiItem[];
    records?: TaskApiItem[];
    count?: number;
    total?: number;
    summary?: TaskSummaryData;
  } | TaskApiItem[];
}

export interface TaskMutationResponse {
  success: boolean;
  statusCode: number;
  message: string;
  data?: TaskApiItem;
}

export interface TaskSummaryData {
  total?: number;
  open?: number;
  assigned?: number;
  in_progress?: number;
  on_hold?: number;
  in_review?: number;
  completed?: number;
  closed?: number;
  cancelled?: number;
  [key: string]: number | undefined;
}

export interface TaskSummaryResponse {
  success: boolean;
  statusCode: number;
  message: string;
  data?: TaskSummaryData | { summary?: TaskSummaryData };
}

export interface CreateTaskPayload {
  title: string;
  description?: string;
  task_type?: TaskType;
  priority?: TaskPriority;
  department_name: string;
  due_date?: string | null;
  start_date?: string | null;
  tags?: string[];
  assigned_to_id?: string;
  assigned_to_name?: string;
  assigned_to_email?: string;
}

export interface UpdateTaskPayload extends Partial<CreateTaskPayload> {}

export interface AssignTaskPayload {
  assigned_to_id: string;
  assigned_to_name: string;
  assigned_to_email?: string;
}

export interface UpdateTaskStatusPayload {
  task_status: TaskStatus;
  remarks?: string;
}

export interface RouteTaskPayload {
  target_department_name: string;
  remarks?: string;
}

export interface AddCommentPayload {
  comment: string;
}

export interface GetTasksParams {
  page?: number;
  limit?: number;
  search?: string;
  task_status?: TaskStatus;
  priority?: TaskPriority;
  task_type?: TaskType;
  department_name?: string;
  assigned_to_id?: string;
  [key: string]: string | number | boolean | undefined;
}

export const getTasks = async (params?: GetTasksParams): Promise<TaskListResponse> => {
  const queryParams: GetTasksParams = {
    ...params,
    search: params?.search?.trim() || undefined,
  };
  return api.get<TaskListResponse>(API_ENDPOINTS.tasks.list, { params: queryParams });
};

export const getMyTasks = async (params?: GetTasksParams): Promise<TaskListResponse> => {
  const queryParams: GetTasksParams = {
    ...params,
    search: params?.search?.trim() || undefined,
  };
  return api.get<TaskListResponse>(API_ENDPOINTS.tasks.myTasks, { params: queryParams });
};

export const getTaskSummary = async (): Promise<TaskSummaryResponse> => {
  return api.get<TaskSummaryResponse>(API_ENDPOINTS.tasks.summary);
};

export const getTaskById = async (id: string): Promise<TaskMutationResponse> => {
  return api.get<TaskMutationResponse>(`${API_ENDPOINTS.tasks.list}/${id}`);
};

export const createTask = async (payload: CreateTaskPayload): Promise<TaskMutationResponse> => {
  return api.post<TaskMutationResponse, CreateTaskPayload>(API_ENDPOINTS.tasks.list, payload);
};

export const updateTask = async (id: string, payload: UpdateTaskPayload): Promise<TaskMutationResponse> => {
  return api.put<TaskMutationResponse, UpdateTaskPayload>(`${API_ENDPOINTS.tasks.list}/${id}`, payload);
};

export const assignTask = async (id: string, payload: AssignTaskPayload): Promise<TaskMutationResponse> => {
  return api.put<TaskMutationResponse, AssignTaskPayload>(`${API_ENDPOINTS.tasks.list}/${id}/assign`, payload);
};

// Internal Tasking: moves a task to another department's queue (e.g. AR sending
// work to Billing Review, or that department sending it back to AR for rework).
export const routeTaskToDepartment = async (id: string, payload: RouteTaskPayload): Promise<TaskMutationResponse> => {
  return api.put<TaskMutationResponse, RouteTaskPayload>(`${API_ENDPOINTS.tasks.list}/${id}/route`, payload);
};

export const updateTaskStatus = async (id: string, payload: UpdateTaskStatusPayload): Promise<TaskMutationResponse> => {
  return api.put<TaskMutationResponse, UpdateTaskStatusPayload>(`${API_ENDPOINTS.tasks.list}/${id}/status`, payload);
};

export const addTaskComment = async (id: string, payload: AddCommentPayload): Promise<TaskMutationResponse> => {
  return api.post<TaskMutationResponse, AddCommentPayload>(`${API_ENDPOINTS.tasks.list}/${id}/comment`, payload);
};

export const deleteTask = async (id: string): Promise<TaskMutationResponse> => {
  return api.delete<TaskMutationResponse>(`${API_ENDPOINTS.tasks.list}/${id}`);
};
